/**
 * Centralized error types for better UI messages and observability
 */

/** Base application error with error code support */
export class AppError extends Error {
  readonly code: string;
  readonly statusCode: number;

  constructor(message: string, code: string, statusCode = 500) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Data integrity error - missing required fields, corrupt data */
export class DataIntegrityError extends AppError {
  constructor(message: string, entityType?: string) {
    super(
      entityType ? `${entityType}: ${message}` : message,
      'DATA_INTEGRITY_ERROR',
      500
    );
    this.name = 'DataIntegrityError';
  }
}

/** Resource not found error */
export class NotFoundError extends AppError {
  constructor(resourceType: string, identifier?: string) {
    super(
      identifier 
        ? `${resourceType} not found: ${identifier}`
        : `${resourceType} not found`,
      'NOT_FOUND',
      404
    );
    this.name = 'NotFoundError';
  }
}

/** Validation error - invalid input format, failed validation */
export class ValidationError extends AppError {
  readonly field?: string;

  constructor(message: string, field?: string) {
    super(message, 'VALIDATION_ERROR', 400);
    this.name = 'ValidationError';
    this.field = field;
  }
}

/** Authentication error - not logged in, invalid token */
export class AuthenticationError extends AppError {
  constructor(message = 'Authentication required') {
    super(message, 'AUTHENTICATION_ERROR', 401);
    this.name = 'AuthenticationError';
  }
}

/** Authorization error - insufficient permissions */
export class AuthorizationError extends AppError {
  constructor(message = 'Insufficient permissions') {
    super(message, 'AUTHORIZATION_ERROR', 403);
    this.name = 'AuthorizationError';
  }
}

/** Rate limit error - too many requests */
export class RateLimitError extends AppError {
  readonly retryAfterMs?: number;

  constructor(message = 'Too many requests', retryAfterMs?: number) {
    super(message, 'RATE_LIMIT_ERROR', 429);
    this.name = 'RateLimitError';
    this.retryAfterMs = retryAfterMs;
  }
}

/** Supabase/Postgrest error shape */
interface PostgrestErrorLike {
  message: string;
  code?: string;
  details?: string | null;
  hint?: string | null;
}

/** Type guard for Postgrest errors */
function isPostgrestError(error: unknown): error is PostgrestErrorLike {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as PostgrestErrorLike).message === 'string'
  );
}

/** Database/network error - preserves Supabase error context */
export class DatabaseError extends AppError {
  readonly dbCode?: string;
  readonly dbDetails?: string | null;
  readonly dbHint?: string | null;

  constructor(message: string, originalError?: unknown) {
    let fullMessage = message;
    const pgError = isPostgrestError(originalError) ? originalError : null;

    if (pgError) {
      const parts = [pgError.message];
      if (pgError.code) parts.push(`code=${pgError.code}`);
      if (pgError.details) parts.push(`details=${pgError.details}`);
      if (pgError.hint) parts.push(`hint=${pgError.hint}`);
      fullMessage = `${message}: ${parts.join(' | ')}`;
    } else if (originalError instanceof Error) {
      fullMessage = `${message}: ${originalError.message}`;
    }

    super(fullMessage, 'DATABASE_ERROR', 500);
    this.name = 'DatabaseError';

    // Preserve structured context for logging/debugging
    if (pgError) {
      this.dbCode = pgError.code;
      this.dbDetails = pgError.details;
      this.dbHint = pgError.hint;
    }
  }
}

/** Razorpay payment/provider error */

/** Razorpay payment/provider error */
export class RazorpayError extends AppError {
  constructor(message = 'Razorpay request failed') {
    super(message, 'RAZORPAY_ERROR', 502);
    this.name = 'RazorpayError';
  }
}

/** Type guard to check if error is an AppError */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/** Get user-friendly message from any error */
export function getUserMessage(error: unknown): string {
  if (isAppError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/** Get error code for logging/analytics */
export function getErrorCode(error: unknown): string {
  if (isAppError(error)) {
    return error.code;
  }
  return 'UNKNOWN_ERROR';
}
